import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import PrinterUtil from '@/utils/print'

interface PrintState {
  loading: boolean
  print: PrinterUtil | null
  connect: boolean
  localhost: boolean
  host: string
  printer: string
  printers: string[]
}

interface PrintActions {
  initial: () => void
  setPrinter: (printer: string) => void
  setPrinters: (printers: string[]) => void
  setHost: (ipaddr: string) => void
  setLocalhost: (localhost: boolean) => void
  reConnect: () => Promise<void>
  loadPrinters: () => Promise<void>
}

type PrintStore = PrintState & PrintActions

const initialState: PrintState = {
  loading: false,
  print: null,
  connect: false,
  localhost: true,
  host: '',
  printer: '',
  printers: []
}

const usePrintStore = create<PrintStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      setPrinter: (printer) => set({ printer: printer }),
      setPrinters: (printers) => set({ printers: printers }),
      // 初始化
      initial: async () => {
        set({ loading: true })
        try {
          const p = new PrinterUtil()
          await p.setup()
          set({ print: p })
          await get().reConnect()
          await get().loadPrinters()
        } catch (error) {
          console.error('❌ 打印机初始化失败:', error)
          set({ connect: false })
        } finally {
          set({ loading: false })
        }
      },
      setHost: async (host) => {
        set({ host })
        await get().reConnect()
        await get().loadPrinters()
      },
      setLocalhost: async (localhost) => {
        set({ localhost })
        await get().reConnect()
        await get().loadPrinters()
      },
      loadPrinters: async () => {
        set({ loading: true })
        try {
          const printers = await get().print?.getPrinters()
          if (printers) {
            get().setPrinters(printers)
            // 如果当前没有选择打印机，设置默认打印机
            if (!get().printer && printers.length > 0) {
              const defaultPrinter = await get().print?.getDefaultPrinter()
              if (defaultPrinter) {
                get().setPrinter(defaultPrinter)
              } else {
                get().setPrinter(printers[0])
              }
            }
          }
        } catch (error) {
          console.error('加载打印机列表失败:', error)
        } finally {
          set({ loading: false })
        }
      },
      reConnect: async () => {
        set({ loading: true })
        try {
          set({ connect: false })
          await get().print?.disconnect()

          if (get().localhost) {
            await get().print?.connect()
          } else {
            await get().print?.connect(get().host)
          }

          set({ connect: true })
          console.log('✅ 打印机连接状态更新为已连接')
        } catch (error) {
          set({ connect: false })
          console.error('❌ 打印机连接失败:', error)
        } finally {
          set({ loading: false })
        }
      }
    }),
    {
      name: 'print-store'
    }
  )
)

export const usePrintState = () => {
  const loading = usePrintStore((state) => state.loading)
  const print = usePrintStore((state) => state.print)
  const printer = usePrintStore((state) => state.printer)
  const printers = usePrintStore((state) => state.printers)
  const host = usePrintStore((state) => state.host)
  const localhost = usePrintStore((state) => state.localhost)
  const connect = usePrintStore((state) => state.connect)

  return { loading, printer, printers, print, host, localhost, connect }
}

export const usePrintActions = () => {
  const setPrinter = usePrintStore((state) => state.setPrinter)
  const setHost = usePrintStore((state) => state.setHost)
  const setLocalhost = usePrintStore((state) => state.setLocalhost)
  const initial = usePrintStore((state) => state.initial)
  const reConnect = usePrintStore((state) => state.reConnect)
  const loadPrinters = usePrintStore((state) => state.loadPrinters)
  return { setPrinter, initial, setHost, setLocalhost, reConnect, loadPrinters }
}
